import { safeLocalStorage } from "./safeStorage";

/**
 * Shared localStorage store for recent hotel booking attempts.
 *
 * localStorage is scoped to the origin and shared across tabs, but not across
 * different browsers, devices, or origins. Records contain only a booking
 * signature and lifecycle metadata; no personally identifiable information is
 * stored. Records expire after ten minutes and are pruned whenever the store
 * is read.
 */
export const DUPLICATE_BOOKING_STORAGE_KEY = "hopper_recent_hotel_bookings";

/** Recent booking records expire after ten minutes. */
export const DUPLICATE_BOOKING_TTL_MS = 10 * 60_000;

export type DuplicateBookingStatus = "pending" | "completed";

export type DuplicateBookingRecord = {
  signature: string;
  status: DuplicateBookingStatus;
  at: number;
};

const isValidRecord = (value: unknown): value is DuplicateBookingRecord => {
  if (value == null || typeof value !== "object") {
    return false;
  }
  const record = value as Partial<DuplicateBookingRecord>;
  return (
    typeof record.signature === "string" &&
    (record.status === "pending" || record.status === "completed") &&
    typeof record.at === "number"
  );
};

const readRecords = (): DuplicateBookingRecord[] => {
  try {
    const raw = safeLocalStorage?.getItem(DUPLICATE_BOOKING_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValidRecord) : [];
  } catch {
    // Corrupt JSON, disabled storage (private mode / SSR) — fail open.
    return [];
  }
};

const writeRecords = (records: DuplicateBookingRecord[]): void => {
  try {
    if (records.length === 0) {
      safeLocalStorage?.removeItem(DUPLICATE_BOOKING_STORAGE_KEY);
      return;
    }
    safeLocalStorage?.setItem(
      DUPLICATE_BOOKING_STORAGE_KEY,
      JSON.stringify(records)
    );
  } catch {
    // Quota exceeded / disabled storage — nothing we can do, ignore.
  }
};

const isFresh = (record: DuplicateBookingRecord, now: number): boolean =>
  now - record.at <= DUPLICATE_BOOKING_TTL_MS;

const pruneExpired = (now: number): DuplicateBookingRecord[] => {
  const all = readRecords();
  const fresh = all.filter((record) => isFresh(record, now));
  if (fresh.length !== all.length) {
    writeRecords(fresh);
  }
  return fresh;
};

const recordsForWrite = (now: number): DuplicateBookingRecord[] =>
  readRecords().filter((record) => isFresh(record, now));

/** Records an in-progress/pending booking without extending its TTL. */
export const recordPendingBooking = (
  signature: string | null,
  now: number = Date.now()
): void => {
  if (!signature) {
    return;
  }
  const records = recordsForWrite(now);
  if (records.some((record) => record.signature === signature)) {
    return;
  }
  records.push({ signature, status: "pending", at: now });
  writeRecords(records);
};

/** Records completion and atomically replaces any pending state for the stay. */
export const recordCompletedBooking = (
  signature: string | null,
  now: number = Date.now()
): void => {
  if (!signature) {
    return;
  }
  const records = recordsForWrite(now).filter(
    (record) => record.signature !== signature
  );
  records.push({ signature, status: "completed", at: now });
  writeRecords(records);
};

/** Removes only a pending record; completed records are never cleared here. */
export const clearPendingBooking = (signature: string | null): void => {
  if (!signature) {
    return;
  }
  writeRecords(
    readRecords().filter(
      (record) =>
        !(record.signature === signature && record.status === "pending")
    )
  );
};

/** Returns the fresh status for a hotel signature, or null when none exists. */
export const getDuplicateBookingStatus = (
  signature: string | null,
  now: number = Date.now()
): DuplicateBookingStatus | null => {
  const fresh = pruneExpired(now);
  if (!signature) {
    return null;
  }
  return fresh.find((record) => record.signature === signature)?.status ?? null;
};
